import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useNavigate } from 'react-router-dom';
import { Mic, Play, ListPlus } from 'lucide-react';
import {
  PageHeader,
  Button,
  IconButton,
  StatCard,
  Badge,
  EmptyState,
  Skeleton,
  SkeletonStat,
  SkeletonRow,
  SkeletonChart
} from '../components/ui/index.js';
import { ContinueCard, LastOpenedTab } from '../components/dashboard/ContinueCard.js';
import { ResumeHudCard } from '../components/dashboard/ResumeHudCard.js';
import { WeekChart, DayBarData } from '../components/dashboard/WeekChart.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  getAllPracticeSessions,
  getLocalTabs,
  getLastOpenedTabSetting,
  clearLastOpenedTabSetting,
  getLocalRoutines,
  ensureSampleRoutine,
  hydrateServerSessions,
  type ServerPracticeSession
} from '../db/operations.js';
import { PracticeSession, TabSong, Routine } from '../types/index.js';
import { calculateTehranStats } from '../utils/practiceStats.js';
import { formatSecondsToMMSS, toPersianDigits } from '../utils/timeUtils.js';
import { getCurrentUserId, ROUTINES_CHANGED_EVENT } from '../sync/routineSync.js';
import { apiClient } from '../utils/apiClient.js';
import { getBlockCategoryConfig } from '../utils/blockCategory.js';

function getTimeOfDayGreeting(hour: number = new Date().getHours()): string {
  if (hour >= 5 && hour < 12) return 'صبح بخیر';
  if (hour >= 12 && hour < 17) return 'ظهر بخیر';
  if (hour >= 17 && hour < 21) return 'عصر بخیر';
  return 'شب بخیر';
}

function getUserFirstName(user: { fullName?: string } | null, isGuest: boolean): string {
  if (isGuest || !user || !user.fullName) return 'نوازنده';
  const trimmed = user.fullName.trim();
  if (trimmed.startsWith('نوازنده')) return 'نوازنده';
  const firstWord = trimmed.split(/\s+/)[0];
  return firstWord || 'نوازنده';
}

function formatSessionDate(dateStr?: string): string {
  if (!dateStr) return 'امروز';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now.getTime() - 86400000);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const timeStr = d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `امروز، ${timeStr}`;
    if (isYesterday) return `دیروز، ${timeStr}`;
    return d.toLocaleDateString('fa-IR', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'امروز';
  }
}

function formatRelativeDay(dateStr?: string): string {
  if (!dateStr) return 'دیروز';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'امروز';
    const yesterday = new Date(now.getTime() - 86400000);
    if (d.toDateString() === yesterday.toDateString()) return 'دیروز';
    return d.toLocaleDateString('fa-IR', { weekday: 'long' });
  } catch {
    return 'دیروز';
  }
}

export const DashboardPage: React.FC = () => {
  usePageTitle('داشبورد');
  const navigate = useNavigate();
  const { user, isGuest } = useAuthStore();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [tabs, setTabs] = useState<TabSong[]>([]);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [lastOpenedTab, setLastOpenedTab] = useState<LastOpenedTab | null>(null);
  const [apiStats, setApiStats] = useState<any | null>(null);

  const loadData = useCallback(async () => {
    try {
      await ensureSampleRoutine();
      const currentUid = getCurrentUserId();

      // Server is the source of truth for history when online: hydrate Dexie so
      // chart, recent sessions and stat cards describe the same data (DIFF-010).
      if (navigator.onLine && !isGuest) {
        try {
          const serverSessions = await apiClient.get<ServerPracticeSession[]>('/api/sync/sessions');
          if (Array.isArray(serverSessions)) {
            await hydrateServerSessions(serverSessions);
          }
        } catch {
          // Offline or server error: keep local data only
        }
      }
      const [allSessions, allTabs, allRoutines, lastTabEntry] = await Promise.all([
        getAllPracticeSessions(currentUid),
        getLocalTabs(),
        getLocalRoutines(currentUid),
        getLastOpenedTabSetting()
      ]);

      setSessions(allSessions);
      setTabs(allTabs);
      setRoutines(allRoutines);

      if (lastTabEntry) {
        const val = lastTabEntry.value || ((lastTabEntry as any).tabId ? lastTabEntry : null);
        const lastId = val?.tabId ?? val?.id;
        if (val && lastId && !allTabs.some((t) => t.id === lastId)) {
          // The tab was deleted (or tombstoned): hide the Continue card and forget the pointer.
          setLastOpenedTab(null);
          void clearLastOpenedTabSetting().catch(() => undefined);
        } else {
          setLastOpenedTab(val);
        }
      } else {
        setLastOpenedTab(null);
      }

      // Try fetching online stats if connected
      if (navigator.onLine && !isGuest) {
        try {
          const tz = typeof Intl !== 'undefined' ? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Tehran') : 'Asia/Tehran';
          const stats = await apiClient.get<any>('/api/sync/stats?tz=' + encodeURIComponent(tz));
          if (stats) {
            setApiStats(stats);
          }
        } catch {
          // Fall back to local calculations
          setApiStats(null);
        }
      } else {
        setApiStats(null);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isGuest]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Sync / routines changed listeners
  useEffect(() => {
    const onRefresh = () => {
      void loadData();
    };
    window.addEventListener(ROUTINES_CHANGED_EVENT, onRefresh);
    window.addEventListener('online', onRefresh);
    return () => {
      window.removeEventListener(ROUTINES_CHANGED_EVENT, onRefresh);
      window.removeEventListener('online', onRefresh);
    };
  }, [loadData]);

  // Map block IDs to routine titles for quick lookup
  const routineBlockMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of routines) {
      for (const b of r.blocks || []) {
        map.set(b.id, r.title);
      }
    }
    return map;
  }, [routines]);

  // Calculate local Tehran stats
  const localStats = useMemo(
    () => calculateTehranStats(sessions, routineBlockMap),
    [sessions, routineBlockMap]
  );

  // Aggregate stats (API or fallback)
  const thisWeekSeconds = apiStats?.thisWeekSeconds ?? localStats.thisWeekSeconds;
  const thisWeekActiveSeconds = apiStats?.thisWeekActiveSeconds ?? localStats.thisWeekActiveSeconds;
  const highestCleanBpm = apiStats?.highestCleanBpm ?? localStats.highestCleanBpm;
  const streakDays = apiStats?.streakDays ?? localStats.streakDays;
  const weekOverWeekPercent = apiStats?.weekOverWeekPercent ?? localStats.weekOverWeekPercent;
  const highestCleanBpmBlock = apiStats?.highestCleanBpmBlock ?? localStats.highestCleanBpmBlock;

  // Format hours as h:mm
  const formatHM = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return toPersianDigits(`${h}:${m.toString().padStart(2, '0')}`);
  };

  const weeklyHoursStr = formatHM(thisWeekSeconds);
  const activeHoursStr = formatHM(thisWeekActiveSeconds);
  const activePercent =
    thisWeekSeconds > 0 ? Math.round((thisWeekActiveSeconds / thisWeekSeconds) * 100) : 0;

  // Clean BPM hint
  const cleanBpmHint = useMemo(() => {
    if (highestCleanBpmBlock) {
      const title =
        highestCleanBpmBlock.blockTitle ||
        highestCleanBpmBlock.routineTitle ||
        getBlockCategoryConfig(highestCleanBpmBlock.category as any).label;
      const relative = formatRelativeDay(highestCleanBpmBlock.date);
      return `${title} · ${relative}`;
    }
    return 'رکورد ثبت‌شده در پایان بلوک‌ها';
  }, [highestCleanBpmBlock]);

  // Delta hint for Card 1
  const weekDeltaHint = useMemo(() => {
    if (weekOverWeekPercent === 0) {
      return 'نسبت به هفتهٔ قبل';
    }
    const absVal = Math.abs(weekOverWeekPercent);
    return weekOverWeekPercent > 0
      ? `${toPersianDigits(absVal)}٪ بیشتر از هفتهٔ قبل`
      : `${toPersianDigits(absVal)}٪ کمتر از هفتهٔ قبل`;
  }, [weekOverWeekPercent]);

  // 7-day chart data
  const chartDays: DayBarData[] = useMemo(() => {
    const dayInitials = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
    const currentIdx = localStats.currentDayIndex;
    return localStats.week.map((item, idx) => ({
      dayIndex: idx,
      date: item.date,
      seconds: item.seconds,
      minutes: Math.round(item.seconds / 60),
      isToday: idx === currentIdx,
      dayInitial: dayInitials[idx] || 'ش'
    }));
  }, [localStats.week, localStats.currentDayIndex]);

  // Top 3 active routines
  const topRoutines = useMemo(() => {
    return routines.slice(0, 3);
  }, [routines]);

  // Last executed routine for CTA
  const lastRoutine = useMemo(() => {
    if (routines.length === 0) return null;
    // Check if any recent session matches a routine
    for (const s of sessions) {
      for (const b of s.blocks || []) {
        const found = routines.find((r) => r.blocks?.some((rb) => rb.id === b.blockId));
        if (found) return found;
      }
    }
    return routines[0];
  }, [routines, sessions]);

  const handleStartRoutine = () => {
    if (lastRoutine) {
      navigate(`/routines/${lastRoutine.id}/hud`);
    } else {
      navigate('/routines');
    }
  };

  const firstName = getUserFirstName(user, isGuest);
  const greeting = `${getTimeOfDayGreeting()}، ${firstName}`;
  const streakSubtitle = `این هفته ${toPersianDigits(streakDays)} روز پشت سر هم تمرین کرده‌اید.`;
  const routineDurationMinutes = lastRoutine?.targetDurationMinutes || 30;

  return (
    <div className="space-y-5 lg:space-y-6 pb-24 md:pb-8">
      {/* PageHeader with dynamic Persian greeting & streak */}
      <PageHeader
        title={(<span data-testid="dashboard-greeting">{greeting}</span> as unknown as string)}
        subtitle={
          (<span data-testid="dashboard-streak-subtitle">{streakSubtitle}</span> as unknown as string)
        }
        actionsClassName="hidden md:flex"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              leadingIcon={<Mic className="size-4" />}
              onClick={() => navigate('/tuner')}
              data-testid="dashboard-tune-button"
            >
              کوک کردن
            </Button>
            <Button
              variant="primary"
              size="md"
              leadingIcon={<Play className="size-4 fill-current" />}
              onClick={handleStartRoutine}
              data-testid="dashboard-start-routine-button"
            >
              شروع روتین امروز
            </Button>
          </div>
        }
      />

      {/* Mobile-only full-width CTA button directly below greeting */}
      <div className="md:hidden">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          leadingIcon={<Play className="size-5 fill-current" />}
          onClick={handleStartRoutine}
          data-testid="dashboard-start-routine-button"
          className="min-h-[52px] rounded-xl text-[15px] font-semibold"
        >
          شروع روتین امروز · {toPersianDigits(routineDurationMinutes)} دقیقه
        </Button>
      </div>

      {/* Interrupted Focus HUD session (unmounted when there is nothing to resume) */}
      <ResumeHudCard />

      {/* Continue practice card (unmounted if no last opened tab) */}
      <ContinueCard tabData={lastOpenedTab} />

      {/* 4-Metric StatCards: 4 cols on desktop, 2x2 grid on mobile */}
      <section
        aria-label="آمار این هفته"
        className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-4"
      >
        {isLoading ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          <>
            <StatCard
              label="زمان تمرین این هفته"
              mobileLabel="زمان این هفته"
              value={
                (
                  <span data-testid="stat-weekly-hours" className="font-sans tracking-tight tabular-nums">
                    {weeklyHoursStr}
                  </span>
                ) as unknown as string
              }
              unit="ساعت"
              hint={weekDeltaHint}
              hintTone={weekOverWeekPercent > 0 ? 'success-soft' : 'muted'}
            />

            <StatCard
              label="نوازندگی فعال"
              mobileLabel="نوازندگی فعال"
              value={
                (
                  <span data-testid="stat-active-hours" className="font-sans tracking-tight tabular-nums">
                    {activeHoursStr}
                  </span>
                ) as unknown as string
              }
              unit="ساعت"
              hint={`${toPersianDigits(activePercent)}٪ از زمان جلسه‌ها`}
            />

            <StatCard
              label="بالاترین تمپوی تمیز"
              mobileLabel="تمپوی تمیز"
              value={
                (
                  <span data-testid="stat-clean-bpm" dir="ltr">
                    {highestCleanBpm && highestCleanBpm > 0 ? toPersianDigits(highestCleanBpm) : '—'}
                  </span>
                ) as unknown as string
              }
              unit={highestCleanBpm && highestCleanBpm > 0 ? 'BPM' : undefined}
              tone="accent-soft"
              hint={cleanBpmHint}
            />

            <StatCard
              label="تبلچرهای آفلاین"
              mobileLabel="تبلچر آفلاین"
              value={
                (<span data-testid="stat-offline-tabs">{toPersianDigits(tabs.length)}</span> as unknown as string)
              }
              unit="فایل"
              hint="بدون اینترنت قابل پخش"
            />
          </>
        )}
      </section>

      {/* Content Split Grid (3/5 Routines + 2/5 WeekChart on desktop, WeekChart above Routines on mobile) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-4">
        {/* Routines Card (span 3 on desktop) */}
        <section
          aria-label="روتین‌های من"
          className="col-span-1 lg:col-span-3 order-2 lg:order-1 flex flex-col gap-2.5 md:gap-0 md:bg-base-200 md:border md:border-base-300 md:rounded-2xl md:overflow-hidden md:justify-between"
        >
          {/* Mobile section title (outside the card) */}
          <div className="md:hidden flex items-center justify-between">
            <h2 className="text-[15px] font-semibold text-base-content">روتین‌های من</h2>
            <a
              href="/routines"
              onClick={(e) => {
                e.preventDefault();
                navigate('/routines');
              }}
              className="inline-flex items-center justify-center min-h-11 min-w-11 text-[13px] font-semibold text-primary-soft hover:text-base-content transition-colors"
            >
              همه
            </a>
          </div>

          <div className="hidden md:flex items-center justify-between p-4 lg:px-5 border-b border-base-300">
            <h2 className="text-[15px] md:text-base font-semibold text-base-content">
              روتین‌های من
            </h2>
            <a
              href="/routines"
              onClick={(e) => {
                e.preventDefault();
                navigate('/routines');
              }}
              className="text-xs lg:text-[13px] font-semibold text-primary-soft hover:text-base-content transition-colors"
            >
              همه روتین‌ها
            </a>
          </div>

          <div className="bg-base-200 border border-base-300 rounded-2xl overflow-hidden md:bg-transparent md:border-0 md:rounded-none md:overflow-visible">

          {isLoading ? (
            <div className="divide-y divide-base-300">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : topRoutines.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<ListPlus className="size-6 text-base-content/40" />}
                title="هنوز روتینی نساخته‌اید"
                description="چند بلوک تمرینی کنار هم بچینید و جلسه‌ای زمان‌بندی‌شده بسازید."
                action={
                  <Button variant="primary" size="sm" onClick={() => navigate('/routines')}>
                    ساخت اولین روتین
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="divide-y divide-base-300">
              {topRoutines.map((routine, idx) => (
                <div
                  key={routine.id}
                  onClick={() => navigate(`/routines?id=${routine.id}`)}
                  className="flex items-center gap-3.5 py-3 px-3.5 md:p-3.5 lg:py-3.5 lg:px-5 hover:bg-base-300/30 transition-colors cursor-pointer"
                  data-testid={`dashboard-routine-row-${routine.id}`}
                >
                  {/* Category Timeline Bar (64x8 px) */}
                  <div
                    dir="ltr"
                    className="hidden md:flex w-16 h-2 rounded-full overflow-hidden gap-[3px] shrink-0"
                  >
                    {routine.blocks && routine.blocks.length > 0 ? (
                      routine.blocks.map((block, bIdx) => (
                        <span
                          key={block.id || bIdx}
                          style={{ flex: block.durationMinutes || 1 }}
                          className={getBlockCategoryConfig(block.category).bgClass}
                        />
                      ))
                    ) : (
                      <span className="flex-1 bg-primary" />
                    )}
                  </div>

                  {/* Routine Title and Subtitle */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-base-content truncate">
                      {routine.title}
                    </p>
                    <p className="text-xs text-base-content/60 mt-0.5 truncate">
                      {toPersianDigits(routine.blocks?.length || 0)} بلوک ·{' '}
                      {toPersianDigits(routine.targetDurationMinutes || 0)} دقیقه
                    </p>
                  </div>

                  {/* Play IconButton (first item primary) */}
                  <IconButton
                    label={`اجرای ${routine.title}`}
                    icon={<Play className="size-4 fill-current" />}
                    variant={idx === 0 ? 'primary' : 'secondary'}
                    size="sm"
                    className="size-11 rounded-xl md:size-9 md:rounded-[10px]"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/routines/${routine.id}/hud`);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          </div>
        </section>

        {/* 7-Day Practice Minutes Chart (span 2 on desktop) */}
        <div className="col-span-1 lg:col-span-2 order-1 lg:order-2 flex flex-col [&>*]:flex-1">
          {isLoading ? (
            <SkeletonChart />
          ) : (
            <WeekChart days={chartDays} />
          )}
        </div>
      </div>

      {/* Recent Sessions History */}
      <section
        aria-label="جلسه‌های اخیر"
        className="flex flex-col gap-2.5 md:gap-0 md:bg-base-200 md:border md:border-base-300 md:rounded-2xl md:overflow-hidden"
      >
        <h2 className="md:hidden text-[15px] font-semibold text-base-content">جلسه‌های اخیر</h2>
        <div className="hidden md:flex items-center justify-between p-4 lg:px-5 border-b border-base-300">
          <h2 className="text-[15px] md:text-base font-semibold text-base-content">
            جلسه‌های اخیر
          </h2>
          <span className="text-xs text-base-content/60">
            {toPersianDigits(sessions.length)} جلسه
          </span>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table
            className="w-full text-right text-xs lg:text-[13px] border-collapse min-w-[640px]"
            data-testid="recent-sessions-table"
          >
            <thead>
              <tr className="text-base-content/60 border-b border-base-300/80">
                <th className="font-medium py-2.5 px-5">روتین</th>
                <th className="font-medium py-2.5 px-3">زمان</th>
                <th className="font-medium py-2.5 px-3">مدت</th>
                <th className="font-medium py-2.5 px-3">فعال</th>
                <th className="font-medium py-2.5 px-3">تمپوی تمیز</th>
                <th className="font-medium py-2.5 px-5">وضعیت</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="border-t border-base-300">
                    <td className="py-3 px-5"><Skeleton variant="text" className="w-24 h-4" /></td>
                    <td className="py-3 px-3"><Skeleton variant="text" className="w-20 h-4" /></td>
                    <td className="py-3 px-3"><Skeleton variant="text" className="w-12 h-4" /></td>
                    <td className="py-3 px-3"><Skeleton variant="text" className="w-12 h-4" /></td>
                    <td className="py-3 px-3"><Skeleton variant="text" className="w-10 h-4" /></td>
                    <td className="py-3 px-5"><Skeleton variant="rounded" className="w-14 h-5" /></td>
                  </tr>
                ))
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-xs text-base-content/50">
                    هنوز جلسه‌ای به پایان نرسیده است. یک روتین تمرینی را در Focus HUD اجرا کنید.
                  </td>
                </tr>
              ) : (
                sessions.slice(0, 10).map((s) => {
                  const routineTitle =
                    s.blocks?.find((b) => routineBlockMap.has(b.blockId))
                      ? routineBlockMap.get(s.blocks.find((b) => routineBlockMap.has(b.blockId))!.blockId)
                      : topRoutines[0]?.title || 'تمرین آزاد';
                  const topBpm = s.blocks?.reduce(
                    (max, b) => (b.achievedBpm > max ? b.achievedBpm : max),
                    0
                  ) || 0;

                  return (
                    <tr
                      key={s.clientSessionId}
                      className="border-t border-base-300 hover:bg-base-300/30 transition-colors"
                      data-testid={`session-row-${s.clientSessionId}`}
                    >
                      <td className="py-3 px-5 font-semibold text-base-content">
                        {routineTitle}
                      </td>
                      <td className="py-3 px-3 text-base-content/70">
                        {formatSessionDate(s.createdAt)}
                      </td>
                      <td className="py-3 px-3 font-mono" dir="ltr">
                        {formatSecondsToMMSS(s.totalDurationSeconds)}
                      </td>
                      <td className="py-3 px-3 font-mono text-base-content/70" dir="ltr">
                        {formatSecondsToMMSS(s.activePlaybackSeconds)}
                      </td>
                      <td className="py-3 px-3 font-mono" dir="ltr">
                        {topBpm > 0 ? (
                          <span className="text-accent-soft font-semibold">{topBpm}</span>
                        ) : (
                          <span className="text-base-content/40">—</span>
                        )}
                      </td>
                      <td className="py-3 px-5">
                        <Badge
                          size="sm"
                          tone={s.synced === 1 ? 'success' : 'warning'}
                          dot
                          data-testid={`session-sync-badge-${s.clientSessionId}`}
                        >
                          {s.synced === 1 ? 'همگام' : 'در صف'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile List View */}
        <div className="md:hidden divide-y divide-base-300 bg-base-200 border border-base-300 rounded-2xl overflow-hidden" data-testid="recent-sessions-list">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center text-xs text-base-content/50">
              هنوز جلسه‌ای به پایان نرسیده است. یک روتین تمرینی را در Focus HUD اجرا کنید.
            </div>
          ) : (
            sessions.slice(0, 10).map((s) => {
              const routineTitle =
                s.blocks?.find((b) => routineBlockMap.has(b.blockId))
                  ? routineBlockMap.get(s.blocks.find((b) => routineBlockMap.has(b.blockId))!.blockId)
                  : topRoutines[0]?.title || 'تمرین آزاد';
              const topBpm = s.blocks?.reduce(
                (max, b) => (b.achievedBpm > max ? b.achievedBpm : max),
                0
              ) || 0;

              return (
                <div
                  key={s.clientSessionId}
                  className="py-3 px-3.5 flex items-center justify-between gap-3"
                  data-testid={`session-row-${s.clientSessionId}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-base-content truncate">
                      {routineTitle}
                    </p>
                    <p className="text-xs text-base-content/60 mt-0.5 truncate">
                      {formatSessionDate(s.createdAt)} ·{' '}
                      <span dir="ltr" className="font-mono">
                        {formatSecondsToMMSS(s.totalDurationSeconds)}
                        {topBpm > 0 ? ` · ${topBpm} BPM` : ''}
                      </span>
                    </p>
                  </div>
                  <Badge
                    size="sm"
                    tone={s.synced === 1 ? 'success' : 'warning'}
                    data-testid={`session-sync-badge-${s.clientSessionId}`}
                  >
                    {s.synced === 1 ? 'همگام' : 'در صف'}
                  </Badge>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};
